import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { UploadWarnings } from '../../uploadState';

type UploadWarningAlertsProps = {
    warnings: UploadWarnings;
};

/**
 * What the step points out but lets through, in warning tone: the columns the
 * parse did not read, and an earlier import of the same file.
 */
export const UploadWarningAlerts: React.FC<UploadWarningAlertsProps> = ({
    warnings: { ignoredColumns, previousImport },
}) => {
    const { t } = useTranslation('pages');

    return (
        <>
            {ignoredColumns.length > 0 ? (
                <Alert
                    status="warning"
                    {...getNodeTestIdProp(
                        'catalog-import-upload-warning-ignored',
                    )}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t(
                                'catalogImport.upload.warnings.ignoredColumns.title',
                            )}
                        </Alert.Title>
                        <Alert.Description>
                            {t(
                                'catalogImport.upload.warnings.ignoredColumns.description',
                                { columns: ignoredColumns.join(', ') },
                            )}
                        </Alert.Description>
                    </Alert.Content>
                </Alert>
            ) : null}
            {previousImport ? (
                <Alert
                    status="warning"
                    {...getNodeTestIdProp(
                        'catalog-import-upload-warning-previous',
                    )}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t(
                                'catalogImport.upload.warnings.previousImport.title',
                            )}
                        </Alert.Title>
                        <Alert.Description>
                            {t(
                                'catalogImport.upload.warnings.previousImport.description',
                                {
                                    date: previousImport.importedOn,
                                    branch: previousImport.branchName,
                                },
                            )}
                        </Alert.Description>
                    </Alert.Content>
                </Alert>
            ) : null}
        </>
    );
};
